import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowRight, FlaskConical, Info } from 'lucide-react';
import { useState } from 'react';
import { FormField } from '@/components/shared/form-field';
import { SectionCard } from '@/components/shared/section-card';
import { StatusBadge } from '@/components/shared/tone-badge';
import { NotificationPreview } from '@/components/test-send/notification-preview';
import { ReviewDialog } from '@/components/test-send/review-dialog';
import { TestSendDialog } from '@/components/test-send/test-send-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import type { Message, Send, SendDetail } from '@/lib/types';
import { useAppCtx } from './app-layout';
import { notifyError, useToast } from '@/lib/toast';

export function TestSend() {
  const { app } = useAppCtx();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [actionUrl, setActionUrl] = useState('');
  const [data, setData] = useState('');
  // The last message sent; the API adds up its batches, so one request tracks a whole broadcast.
  const [messageId, setMessageId] = useState<string | null>(null);
  // One key per review: a retried "Send Message" (e.g. after a network blip) returns the same sends.
  const [reviewKey, setReviewKey] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const toast = useToast();

  const send = useMutation({
    mutationFn: (key: string) => {
      const parsed = data.trim() ? (JSON.parse(data) as Record<string, string>) : {};
      // Everyone active in the app: one message, stored as batches of 20 ({ data: Send[] }).
      return api<{ data: Send[] }>(`/admin/v1/apps/${app.id}/broadcast`, {
        method: 'POST',
        body: { idempotency_key: key, title, body, data: parsed, ...(actionUrl ? { action_url: actionUrl } : {}) },
      });
    },
    onSuccess: (r) => { setMessageId(r.data[0]?.group_id ?? null); setReviewKey(null); },
  });

  const tracking = useQuery({
    queryKey: ['send', app.id, messageId],
    enabled: !!messageId,
    queryFn: () => api<SendDetail>(`/admin/v1/apps/${app.id}/sends/${messageId}`),
    refetchInterval: (q) => (q.state.data?.status === 'queued' ? 1500 : false),
  });
  const status = tracking.data ?? null;
  // Both buttons check the Data JSON first, so neither dialog opens on a draft the API would reject.
  const dataIsValid = () => {
    if (!data.trim()) return true;
    try { JSON.parse(data); return true; } catch { notifyError(new Error('Data must be a JSON object of string values')); return false; }
  };
  const review = () => { if (dataIsValid()) setReviewKey(crypto.randomUUID()); };
  const test = () => { if (dataIsValid()) setTesting(true); };
  const message = () => ({
    title, body, data: data.trim() ? (JSON.parse(data) as Record<string, string>) : {}, ...(actionUrl ? { action_url: actionUrl } : {}),
  });

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_22rem]">
      <SectionCard title="Send notification">
        <div className="grid gap-5">
          <FormField label="Title" htmlFor="title"><Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={256} placeholder="e.g. Your order has shipped" /></FormField>
          <FormField label="Description" htmlFor="body"><Textarea id="body" rows={3} value={body} onChange={(e) => setBody(e.target.value)} maxLength={2048} placeholder="e.g. Order #18823 is on its way and will arrive tomorrow." /></FormField>
          <FormField label="Action URL (optional)" hint="Rewritten to a tracked link so taps are counted." htmlFor="url">
            <Input id="url" value={actionUrl} onChange={(e) => setActionUrl(e.target.value)} placeholder="https://example.com/orders/1 or myapp://orders/1" />
          </FormField>
          <FormField label="Data (optional JSON)" htmlFor="data">
            <Textarea id="data" rows={3} className="font-mono text-xs" value={data} onChange={(e) => setData(e.target.value)} placeholder='{ "order_id": "18823" }' />
          </FormField>
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" disabled={!title.trim()} onClick={test}><FlaskConical /> Test and preview</Button>
            <Button disabled={!title.trim()} onClick={review}>Review and send <ArrowRight /></Button>
          </div>
        </div>
      </SectionCard>

      <ReviewDialog
        open={!!reviewKey} onOpenChange={(o) => !o && setReviewKey(null)}
        app={app} title={title} body={body} actionUrl={actionUrl} data={data}
        sending={send.isPending} onSend={() => reviewKey && send.mutate(reviewKey)}
      />
      {testing && (
        <TestSendDialog
          open={testing} onOpenChange={setTesting} app={app} message={message()}
          onSent={(m: Message) => {
            setMessageId(m.id);
            toast(`Test sent to ${m.target_count} ${m.target_count === 1 ? 'device' : 'devices'}`);
          }}
        />
      )}

      <div className="grid h-fit gap-6">
        <SectionCard title="Preview" description="Updates as you type.">
          <NotificationPreview appName={app.name} title={title} body={body} actionUrl={actionUrl} data={data} />
        </SectionCard>

        <SectionCard title="Result" className="h-fit">
          {!status ? <p className="text-sm text-muted-foreground">Send a notification to see its delivery outcome here.</p> : (
            <div className="space-y-3 text-sm">
              <ResultRow k="Status"><StatusBadge status={status.status} /></ResultRow>
              {status.batch_count > 1 && <ResultRow k="Batches"><b className="tabular-nums">{status.batch_count}</b></ResultRow>}
              <ResultRow k="Targets"><b className="tabular-nums">{status.target_count}</b></ResultRow>
              <ResultRow k="Accepted"><b className="tabular-nums">{status.accepted_count}</b></ResultRow>
              <ResultRow k="Failed"><b className="tabular-nums">{status.failed_count}</b></ResultRow>
              {status.error_code && <ResultRow k="Error"><code className="font-mono text-xs">{status.error_code}</code></ResultRow>}
              <p className="flex gap-2 rounded-md bg-muted p-3 text-xs text-muted-foreground"><Info className="size-4 shrink-0" />"Accepted" means FCM/APNs took the message for delivery — providers don't report arrival.</p>
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}

const ResultRow = ({ k, children }: { k: string; children: React.ReactNode }) => (
  <div className="flex items-center justify-between"><span className="text-muted-foreground">{k}</span>{children}</div>
);
